/**
 * Map narration project segments ↔ story scene shape for SceneGridEditor.
 *
 * Canonical still field is narration_segments.image_url (with image_s3_url /
 * image_local_url companions). Scene View, Editor View, and ComfyUI/Workflow
 * all resolve the playable still through narrationSceneImageUrl /
 * narrationSceneImagePlaybackUrl so the same DB row paints everywhere.
 */

import { isSongBasedNarrationProject } from '@/lib/songBasedVideoEditor';
import { mediaUrlForPlayback, mediaUrlForSegmentField } from '@/lib/mediaUrls';

function isFileMediaRef(url) {
  return String(url || '').trim().startsWith('file://');
}

/**
 * Raw still URL from a segment/scene document (no origin rewrite).
 * Order: image_url → image_s3_url → source_image_url → first visual beat.
 */
export function narrationSceneImageUrl(segment) {
  const candidates = [
    segment?.image_url,
    segment?.image_s3_url,
    segment?.source_image_url,
    segment?.source_image_s3_url,
  ];
  for (const candidate of candidates) {
    const url = String(candidate || '').trim();
    if (url && !isFileMediaRef(url)) return url;
  }
  const beats = Array.isArray(segment?.visual_beats) ? segment.visual_beats : [];
  for (const beat of beats) {
    const url = String(beat?.image_url || beat?.imageUrl || '').trim();
    if (url && !isFileMediaRef(url)) return url;
  }
  return null;
}

/** Browser-ready still URL for Scene / Editor / Workflow UI. */
export function narrationSceneImagePlaybackUrl(segment) {
  if (!segment) return null;
  // Prefer the image_url triplet when it can resolve (active version + s3/local).
  const fromImageField = mediaUrlForSegmentField(segment, 'image_url');
  if (fromImageField) return fromImageField;
  const fromSource = mediaUrlForSegmentField(segment, 'source_image_url');
  if (fromSource) return fromSource;
  const raw = narrationSceneImageUrl(segment);
  if (!raw) return null;
  return mediaUrlForPlayback(raw) || null;
}

export function narrationSegmentToScene(segment, project) {
  const dur = Number(segment?.audio_duration ?? segment?.duration) || 0;
  const isSong = isSongBasedNarrationProject(project);
  let audioTimeline = Array.isArray(segment?.audio_timeline) ? segment.audio_timeline : [];
  const text = String(segment?.text || '').trim();
  const displayText = isSong
    ? String(segment?.authored_script || segment?.song_lyric_stanza || '').trim()
    : String(segment?.authored_script || text || '').trim();
  const singleNarrationTimeline = audioTimeline.length === 1
    && String(audioTimeline[0]?.kind || 'narration').toLowerCase() === 'narration';

  // Song flow uses one master track in the video editor — do not synthesize per-segment audio clips.
  if (
    !isSong
    && segment?.audio_url
    && text
    && (!audioTimeline.length || singleNarrationTimeline)
  ) {
    const end = dur > 0 ? dur : Math.max(1, text.split(/\s+/).filter(Boolean).length / 2.5);
    audioTimeline = [
      {
        kind: 'narration',
        text,
        start_seconds: 0,
        end_seconds: end,
        duration_seconds: end,
        index: 0,
        segment_audio_url: segment.audio_url,
      },
    ];
  } else if (!audioTimeline.length && text) {
    const end = dur > 0 ? dur : Math.max(1, text.split(/\s+/).filter(Boolean).length / 2.5);
    audioTimeline = [
      {
        kind: 'narration',
        text,
        start_seconds: 0,
        end_seconds: end,
        duration_seconds: end,
        index: 0,
        segment_audio_url: segment?.audio_url || null,
      },
    ];
  }

  const segNum = segment?.segment_number ?? segment?.scene_number;
  return {
    ...segment,
    id: segment.id,
    scene_number: segNum,
    segment_number: segNum,
    scene_title: segment?.title || `Segment ${segNum ?? ''}`.trim() || 'Segment',
    title: segment?.title,
    scene_text: displayText,
    narration_text: displayText,
    image_url: narrationSceneImageUrl(segment),
    video_url: segment?.video_url || null,
    video_duration_seconds: segment?.video_duration_seconds ?? null,
    audio_url: segment?.audio_url || null,
    sound_effect_url: segment?.sound_effect_url || null,
    image_history: segment?.image_history || [],
    video_history: segment?.video_history || [],
    audio_history: segment?.audio_history || [],
    audio_timeline: audioTimeline,
    audio_duration_seconds: segment?.audio_duration ?? segment?.duration ?? null,
    camera_direction: segment?.camera_direction || segment?.visual || '',
    visual: segment?.visual || '',
    word_timestamps: segment?.word_timestamps || [],
    dialogue_scene_video: segment?.dialogue_scene_video || null,
    multishot_images: Array.isArray(segment?.multishot_images) ? segment.multishot_images : [],
    multishot_count: Number(segment?.multishot_count) || 0,
    multishot_include_master: segment?.multishot_include_master !== false,
    song_based_segment: isSong || Boolean(segment?.song_based_segment),
    audio_motion_timeline: segment?.audio_motion_timeline || null,
    motion_preset: segment?.motion_preset || null,
    motion_strength: segment?.motion_strength ?? null,
    _narrationSegment: true,
    _narrationProjectId: project?.id,
  };
}

export function narrationProjectToStory(project) {
  const input = project?.input || {};
  const videoProvider = input.video_provider || 'narration_slideshow';
  const isSong = (project?.app || '').toLowerCase() === 'song_based'
    || (input.project_kind || '').toLowerCase() === 'song_based';
  return {
    id: project?.id,
    title: project?.title || input.topic || 'Narration',
    user_topic: input.topic || '',
    visual_style: input.visual_style,
    language: input.language || 'en',
    voice_style: input.voice_style || 'storyteller',
    tts_provider: input.tts_provider || 'auto',
    narrator_elevenlabs_voice_id: input.narrator_elevenlabs_voice_id || '',
    narrator_murf_voice_id: input.narrator_murf_voice_id || '',
    narrator_murf_locale: input.narrator_murf_locale || '',
    narrator_minimax_voice_id: input.narrator_minimax_voice_id || '',
    image_provider: input.image_provider || 'nano_banana',
    image_aspect_ratio: input.image_aspect_ratio || '16:9',
    default_scene_video_provider:
      videoProvider === 'narration_slideshow' ? 'nano_banana' : videoProvider,
    _narrationVideoProvider: videoProvider,
    music_url: project?.music_url || null,
    app: project?.app || null,
    project_kind: input.project_kind || null,
    input,
    story_type: isSong ? 'song_based_project' : 'narration',
    characters: [],
  };
}

export function narrationSegmentsFromProject(project) {
  const segments = Array.isArray(project?.segments) ? project.segments : [];
  return segments
    .slice()
    .sort((a, b) => (Number(a.segment_number) || 0) - (Number(b.segment_number) || 0))
    .map((seg) => narrationSegmentToScene(seg, project));
}

export function narrationSegmentId(segment) {
  return String(segment?.id || '').trim();
}

/** Merge a fresh narration segment into Scene View cards (id-safe). */
export function mergeNarrationSegmentIntoScenes(scenes, segment, project) {
  if (!segment || !narrationSegmentId(segment)) return scenes || [];
  const scene = narrationSegmentToScene(segment, project);
  const sid = narrationSegmentId(scene);
  return (scenes || []).map((s) => (narrationSegmentId(s) === sid ? { ...s, ...scene } : s));
}

/**
 * Classify POST /regenerate-image JSON so Scene View can apply a segment,
 * poll a job, or show a retry — instead of silently no-op'ing.
 */
export function narrationImageGenerationResult(data) {
  if (!data || typeof data !== 'object') return { kind: 'empty' };
  const status = String(data.status || '').trim().toLowerCase();
  if (status === 'retry_scheduled' || status === 'retry' || status === 'queued') {
    return {
      kind: 'retry',
      message: data.message || 'Image provider is busy. Your image was queued for automatic retry.',
      segmentId: data.segment_id || null,
    };
  }
  if (data.job_id && !data.id) {
    return { kind: 'job', jobId: data.job_id };
  }
  if (data.id) return { kind: 'segment', segment: data };
  if (data.segment?.id) return { kind: 'segment', segment: data.segment };
  return { kind: 'unknown', data };
}
